import {
  ActionIcon,
  Anchor,
  Button,
  CopyButton,
  Group,
  Paper,
  Radio,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconCopy, IconExternalLink, IconTrash } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DeleteChannelFinalConfirmationModal } from '@/components/DeleteChannelFinalConfirmationModal'
import { DeleteChannelModal } from '@/components/DeleteChannelModal'
import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import { navigateTo } from '@/utils/navigation'
import { getChannelDeeplinkUrl, getChannelRegistrationUrl } from '@/utils/sponsors'

interface AccessPanelProps {
  registerUnsavedChangesChecker: (tabKey: 'access', checker: () => boolean, onDiscard: () => void) => void
}

const IMPLEMENTATION_KIT_URL = 'https://sidekickplatform.com/resources/implementation-kit/'

const GUEST_TASTER_MESSAGE_LIMIT = 10

function isValidUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

export function AccessPanel({ registerUnsavedChangesChecker }: AccessPanelProps) {
  const { api, authenticationStore, configStore } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)

  const channelUrl = getChannelDeeplinkUrl(configStore.authBaseUrl, authenticationStore.user?.sponsorId)
  const registrationUrl = getChannelRegistrationUrl(authenticationStore.user?.sponsorId)

  const [showDeleteChannelModal, setShowDeleteChannelModal] = useState(false)
  const [showFinalDeleteConfirmModal, setShowFinalDeleteConfirmModal] = useState(false)
  const [isDeletingChannel, setIsDeletingChannel] = useState(false)

  const sponsorInfoQuery = useQuery({
    queryKey: ['sponsor', 'info'],
    queryFn: () => api.getSponsorInfo(),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const [originalUrl, setOriginalUrl] = useState<string>('')
  const [subscriptionUrl, setSubscriptionUrl] = useState<string>('')
  const [originalSelfReg, setOriginalSelfReg] = useState<boolean>(false)
  const [selfRegEnabled, setSelfRegEnabled] = useState<boolean>(false)
  const [originalGuestUserAccess, setOriginalGuestUserAccess] = useState<boolean>(false)
  const [guestUserAccess, setGuestUserAccess] = useState<boolean>(false)
  const hasEditsRef = useRef(false)

  useEffect(() => {
    if (sponsorInfoQuery.data) {
      const url = sponsorInfoQuery.data.subscriptionUrl ?? ''
      const selfReg = sponsorInfoQuery.data.selfRegistrationEnabled ?? false
      const guest = sponsorInfoQuery.data.guestUserAccess ?? false
      setOriginalUrl(url)
      setOriginalSelfReg(selfReg)
      setOriginalGuestUserAccess(guest)
      if (!hasEditsRef.current) {
        setSubscriptionUrl(url)
        setSelfRegEnabled(selfReg)
        setGuestUserAccess(guest)
      }
    }
  }, [sponsorInfoQuery.data])

  const hasChanges = useMemo(() => {
    return (
      originalUrl !== subscriptionUrl ||
      originalSelfReg !== selfRegEnabled ||
      originalGuestUserAccess !== guestUserAccess
    )
  }, [originalUrl, subscriptionUrl, originalSelfReg, selfRegEnabled, originalGuestUserAccess, guestUserAccess])

  useEffect(() => {
    hasEditsRef.current = hasChanges
  }, [hasChanges])

  const handleDiscard = useCallback(() => {
    setSubscriptionUrl(originalUrl)
    setSelfRegEnabled(originalSelfReg)
    setGuestUserAccess(originalGuestUserAccess)
  }, [originalUrl, originalSelfReg, originalGuestUserAccess])

  const handleCancel = useCallback(() => {
    if (!hasChanges) return
    modals.openConfirmModal({
      title: 'Discard Changes',
      centered: true,
      children: 'You have unsaved changes. Are you sure you want to discard them?',
      labels: { confirm: 'Discard', cancel: 'Keep Editing' },
      confirmProps: { color: 'red' },
      groupProps: { mt: 'xl' },
      onConfirm: handleDiscard,
    })
  }, [hasChanges, handleDiscard])

  useEffect(() => {
    registerUnsavedChangesChecker('access', () => hasChanges, handleDiscard)
  }, [registerUnsavedChangesChecker, hasChanges, handleDiscard])

  const handleSave = useCallback(async () => {
    const trimmed = subscriptionUrl.trim()
    if (trimmed && !isValidUrl(trimmed)) {
      notifications.show({
        title: 'Invalid URL',
        message: 'Please enter a valid URL (e.g. https://example.com).',
        color: 'red',
      })
      return
    }

    setIsPending(true)
    try {
      if (trimmed !== originalUrl) {
        await api.updateSubscriptionUrl(trimmed || null)
        setOriginalUrl(trimmed)
        setSubscriptionUrl(trimmed)
      }
      if (selfRegEnabled !== originalSelfReg) {
        await api.updateSelfRegistrationEnabled(selfRegEnabled)
        setOriginalSelfReg(selfRegEnabled)
      }
      if (guestUserAccess !== originalGuestUserAccess) {
        await api.updateGuestUserAccess(guestUserAccess)
        setOriginalGuestUserAccess(guestUserAccess)
      }
      await api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'info'] })
      notifications.show({
        title: 'Settings Saved',
        message: 'Your channel access settings have been updated.',
        color: 'green',
      })
    } catch (error) {
      console.error('Error saving access settings:', error)
      notifications.show({
        title: 'Save Failed',
        message: 'Could not save access settings. Please try again.',
        color: 'red',
      })
    } finally {
      setIsPending(false)
    }
  }, [api, subscriptionUrl, originalUrl, selfRegEnabled, originalSelfReg, guestUserAccess, originalGuestUserAccess])

  // -------------------------------------------------------------------------
  // Delete channel (sponsor) -- decoupled from the access save flow; it has its
  // own modals and loading state. On success we log the user out and bounce to
  // login with a success banner.
  // -------------------------------------------------------------------------

  const channelNameForDelete = (sponsorInfoQuery.data?.name ?? authenticationStore.user?.sponsorName ?? '').trim()

  const handleFirstDeleteConfirm = () => {
    setShowDeleteChannelModal(false)
    setShowFinalDeleteConfirmModal(true)
  }

  const handleDeleteChannel = async () => {
    const sponsorId = authenticationStore.user?.sponsorId
    if (!sponsorId) {
      notifications.show({
        title: 'Could Not Delete Channel',
        message: 'Missing channel context. Please refresh the page and try again.',
        color: 'red',
      })
      return
    }

    setIsDeletingChannel(true)
    try {
      await api.deleteSponsor(sponsorId)
    } catch (error) {
      console.error('Failed to delete channel:', error)
      notifications.show({
        title: 'Could Not Delete Channel',
        message:
          error instanceof Error && error.message
            ? error.message
            : 'Something went wrong while deleting your channel. Please try again.',
        color: 'red',
      })
      setIsDeletingChannel(false)
      return
    }

    try {
      await authenticationStore.logOut()
    } catch (error) {
      console.warn('Logout after channel deletion failed; clearing session anyway.', error)
    }

    setIsDeletingChannel(false)
    setShowFinalDeleteConfirmModal(false)

    navigateTo('login', {
      searchParams: {
        channelDeleted: '1',
        ...(channelNameForDelete ? { channelName: channelNameForDelete } : {}),
      },
    })
  }

  if (sponsorInfoQuery.isLoading || sponsorInfoQuery.isError) {
    return (
      <Stack>
        <Paper radius="md" p="lg" shadow="sm" px="xl">
          <Group justify="space-between" align="center" p="md">
            <Title order={4}>Manage Channel Access</Title>
            {sponsorInfoQuery.isLoading && <Text>Loading...</Text>}
            {sponsorInfoQuery.isError && <Text>Error loading data</Text>}
          </Group>
        </Paper>
      </Stack>
    )
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" align="flex-start" mb="lg" wrap="nowrap">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Title order={isMobile ? 6 : 4}>Manage Channel Access</Title>
            <Text size="sm" c="dimmed">
              Control how users try, register for, and access this channel.
            </Text>
          </Stack>
          <Group wrap="nowrap">
            <Button variant="outline" onClick={handleCancel} disabled={!hasChanges || isPending}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!hasChanges || isPending} loading={isPending}>
              Save
            </Button>
          </Group>
        </Group>

        <Stack gap="lg">
          {/* ---- Registration Method ---- */}
          <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder>
            <Stack gap="md">
              <Stack gap={4}>
                <Title order={isMobile ? 6 : 5}>Registration Method</Title>
                <Text size="sm" c="dimmed">
                  Choose how users get an account for this channel.
                </Text>
              </Stack>

              <Radio.Group
                value={selfRegEnabled ? 'self' : 'invite'}
                onChange={value => setSelfRegEnabled(value === 'self')}>
                <Stack gap="md">
                  <Radio
                    value="invite"
                    disabled={isPending}
                    label="Invite only"
                    description="Program Managers must invite users from the People page."
                  />

                  {!selfRegEnabled && (
                    <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder ml={isMobile ? 0 : 'xl'}>
                      <Text fw={600} mb={4}>
                        Request Access Link{' '}
                        <Text span c="dimmed" fw={400}>
                          (Optional)
                        </Text>
                      </Text>
                      <Text size="sm" mb="sm">
                        Provide a link where users can request access to your channel. This can be a subscription
                        management system or a simple form or signup page that you manage.
                      </Text>
                      <TextInput
                        label="Add your link here:"
                        value={subscriptionUrl}
                        onChange={e => setSubscriptionUrl(e.target.value)}
                        placeholder="https://..."
                        disabled={isPending}
                        size="sm"
                        maw={500}
                        mb="sm"
                      />
                      <Text size="sm" c="dimmed">
                        After users submit the form or subscribe, you must invite them through the{' '}
                        <Text span fw={700}>
                          People
                        </Text>{' '}
                        page in this Sidekick Portal.
                      </Text>
                    </Paper>
                  )}

                  <Radio
                    value="self"
                    disabled={isPending}
                    label="Allow self-registration"
                    description="Users can create their own account using the channel registration link. Program Managers can still invite users from People page."
                  />

                  {selfRegEnabled && registrationUrl && (
                    <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder ml={isMobile ? 0 : 'xl'}>
                      <Text size="sm" mb="xs">
                        Share this registration link with your users:
                      </Text>
                      <Group gap="xs" maw={500}>
                        <TextInput
                          value={registrationUrl}
                          readOnly
                          flex={1}
                          size="sm"
                          styles={{ input: { fontFamily: 'monospace' } }}
                        />
                        <CopyButton value={registrationUrl} timeout={2000}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                              <ActionIcon
                                color={copied ? 'teal' : 'gray'}
                                variant="subtle"
                                onClick={copy}
                                size="lg"
                                aria-label={copied ? 'Registration URL copied' : 'Copy registration URL'}>
                                {copied ? (
                                  <IconCheck size={18} aria-hidden="true" />
                                ) : (
                                  <IconCopy size={18} aria-hidden="true" />
                                )}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                        <Tooltip label="Open in new tab" withArrow>
                          <ActionIcon
                            component="a"
                            href={registrationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            color="gray"
                            variant="subtle"
                            size="lg"
                            aria-label="Open registration URL in new tab">
                            <IconExternalLink size={18} aria-hidden="true" />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Paper>
                  )}
                </Stack>
              </Radio.Group>
            </Stack>
          </Paper>

          {/* ---- Guest User Access (PM-only) ---- */}
          {authenticationStore.isSponsorAdmin && (
            <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder>
              <Stack gap="md">
                <Title order={isMobile ? 6 : 5}>Guest User Access</Title>
                <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                  <Switch
                    checked={guestUserAccess}
                    onChange={e => setGuestUserAccess(e.currentTarget.checked)}
                    label="Enable Guest User Access"
                    disabled={isPending}
                    aria-label="Enable Guest User Access"
                  />
                  <Text size="sm" c="dimmed">
                    Anyone with the link can view your channel&apos;s Content Library, access free content, and try
                    Sidekicks for up to {sponsorInfoQuery.data?.guestMessageLimit ?? GUEST_TASTER_MESSAGE_LIMIT}{' '}
                    messages without logging in.
                  </Text>
                </Stack>
              </Stack>
            </Paper>
          )}

          {/* ---- Web Setup ---- */}
          <Paper radius="md" p={isMobile ? 'md' : 'lg'} withBorder>
            <Stack gap="md">
              <Stack gap={4}>
                <Title order={isMobile ? 6 : 5}>Web Setup</Title>
                <Text size="sm" c="dimmed">
                  Make it easy for users to access your Sidekick channel from your website.
                </Text>
              </Stack>

              <Stack gap="lg">
                <div>
                  <Text fw={600} mb="xs">
                    1. Add the channel link to your website
                  </Text>
                  <Text size="sm" mb="sm">
                    Add this link to a button, menu item, or call-to-action anywhere on your site. It will guide users
                    directly to your channel on the Sidekick Platform.
                  </Text>
                  {channelUrl && (
                    <Group gap="xs" maw={500}>
                      <TextInput
                        value={channelUrl}
                        readOnly
                        flex={1}
                        size="sm"
                        styles={{ input: { fontFamily: 'monospace' } }}
                      />
                      <CopyButton value={channelUrl} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow>
                            <ActionIcon
                              color={copied ? 'teal' : 'gray'}
                              variant="subtle"
                              onClick={copy}
                              size="lg"
                              aria-label={copied ? 'Channel URL copied' : 'Copy channel URL'}>
                              {copied ? (
                                <IconCheck size={18} aria-hidden="true" />
                              ) : (
                                <IconCopy size={18} aria-hidden="true" />
                              )}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                      <Tooltip label="Open in new tab" withArrow>
                        <ActionIcon
                          component="a"
                          href={channelUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="gray"
                          variant="subtle"
                          size="lg"
                          aria-label="Open channel URL in new tab">
                          <IconExternalLink size={18} aria-hidden="true" />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </div>

                <div>
                  <Text fw={600} mb="xs">
                    2. Build your website button
                  </Text>
                  <Text size="sm">
                    For button designs, assets, and step-by-step setup instructions, visit the{' '}
                    <Anchor href={IMPLEMENTATION_KIT_URL} target="_blank" rel="noopener noreferrer">
                      Sidekick Implementation Kit
                    </Anchor>
                    .
                  </Text>
                </div>
              </Stack>
            </Stack>
          </Paper>

          {/* ---- Delete this Channel (PM-only) ---- */}
          {authenticationStore.isSponsorAdmin && (
            <Paper
              radius="md"
              p={isMobile ? 'md' : 'lg'}
              withBorder
              style={{ borderColor: 'var(--mantine-color-red-3)' }}>
              <Stack gap="md">
                <Title order={isMobile ? 6 : 5} c="red.7">
                  Delete this Channel
                </Title>
                <Group justify="space-between" align="flex-start" gap="md" wrap={isMobile ? 'wrap' : 'nowrap'}>
                  <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" c="dimmed">
                      Permanently deactivate this channel and all of its content. End users will lose access
                      immediately. This action cannot be undone.
                    </Text>
                  </Stack>
                  <Button
                    color="red"
                    variant="outline"
                    leftSection={<IconTrash size={16} />}
                    onClick={() => setShowDeleteChannelModal(true)}
                    disabled={isPending || isDeletingChannel || !channelNameForDelete}>
                    Delete Channel
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Paper>

      <DeleteChannelModal
        opened={showDeleteChannelModal}
        onClose={() => setShowDeleteChannelModal(false)}
        onConfirm={handleFirstDeleteConfirm}
        channelName={channelNameForDelete}
      />

      <DeleteChannelFinalConfirmationModal
        opened={showFinalDeleteConfirmModal}
        onClose={() => setShowFinalDeleteConfirmModal(false)}
        onConfirm={handleDeleteChannel}
        channelName={channelNameForDelete}
        isLoading={isDeletingChannel}
      />
    </Stack>
  )
}
